import React, { useMemo, useState } from "react";
import { Calendar as CalIcon, Plus } from "lucide-react";
import DashboardSectionCard from "@/components/dashboard/DashboardSectionCard";
import DashboardCardAction from "@/components/dashboard/DashboardCardAction";
import { Button } from "@/components/ui/button";
import { cn, formatTime } from "@/lib/utils";
import { useLang } from "@/i18n";

const TYPE_COLOR = {
  "follow-up": "bg-warning",
  relance: "bg-warning",
  call: "bg-info",
  meeting: "bg-info",
  match: "bg-success",
  email: "bg-info",
};

function weekDays() {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const DAY_LABELS_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function WeeklyCalendarCard({ agenda = [], onItemClick, onAddEvent }) {
  const { t, lang } = useLang();
  const dayLabels = lang === "en" ? DAY_LABELS_EN : DAY_LABELS;
  const days = useMemo(weekDays, []);
  const today = new Date();
  const [selected, setSelected] = useState(() => new Date());

  const dayMap = useMemo(() => {
    const m = {};
    agenda.forEach((it) => {
      const k = new Date(it.at).toDateString();
      (m[k] = m[k] || []).push(it);
    });
    return m;
  }, [agenda]);

  const items = (dayMap[selected.toDateString()] || []).slice().sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <DashboardSectionCard
      title={t("nav.calendar")}
      icon={CalIcon}
      iconClass="text-info"
      testid="card-weekly-calendar"
      className="lg:min-h-[520px] xl:min-h-0 xl:h-[clamp(470px,31vw,570px)]"
      footer={
        <DashboardCardAction to="/calendar" testid="week-see-full-calendar">
          {t("dash.card.seeFullCalendar")}
        </DashboardCardAction>
      }
    >
      {/* Week strip */}
      <div className="grid grid-cols-7 gap-1 mt-2 mb-3 shrink-0" data-testid="week-strip">
        {days.map((d, i) => {
          const isToday = d.toDateString() === today.toDateString();
          const isSelected = d.toDateString() === selected.toDateString();
          const has = (dayMap[d.toDateString()] || []).length > 0;
          return (
            <button
              key={i}
              onClick={() => setSelected(d)}
              aria-selected={isSelected || undefined}
              className={cn(
                "flex flex-col items-center gap-0.5 py-1.5 rounded-xl transition-colors",
                isSelected && !isToday && "bg-secondary",
                !isSelected && "hover:bg-secondary/60"
              )}
              data-testid={`week-day-${i}`}
            >
              <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{dayLabels[i]}</span>
              <span
                className={cn(
                  "h-7 w-7 flex items-center justify-center rounded-full text-sm tabular-nums font-medium",
                  isToday ? "bg-athlynx text-white font-bold" : "text-foreground/90",
                  isSelected && !isToday && "ring-1 ring-foreground/30"
                )}
              >
                {d.getDate()}
              </span>
              <span className={cn("h-1 w-1 rounded-full", has ? "bg-info" : "bg-transparent")} aria-hidden />
            </button>
          );
        })}
      </div>

      {/* Day timeline */}
      <div className="flex-1 min-h-0 flex flex-col mt-3 xl:mt-[18px]">
        {items.length === 0 ? (
          <div className="flex-1 min-h-[200px] xl:min-h-0 flex flex-col items-center justify-center gap-3 rounded-[14px] border border-dashed border-border text-center" data-testid="week-day-empty">
            <CalIcon className="h-8 w-8 text-muted-foreground/40" aria-hidden />
            <div className="text-sm text-muted-foreground">{t("dash.card.noEventsDay")}</div>
            <Button size="sm" variant="outline" onClick={onAddEvent} className="h-10 text-sm gap-1.5 px-4" data-testid="week-add-event">
              <Plus className="h-3.5 w-3.5" /> {t("dash.card.createEvent")}
            </Button>
          </div>
        ) : (
          <ol className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1" data-testid="week-day-items">
            {items.slice(0, 6).map((it) => {
              const overdue = new Date(it.at) < new Date() && it.kind === "reminder";
              return (
                <li key={`${it.kind}-${it.id}`}>
                  <button
                    onClick={() => onItemClick?.(it)}
                    className="w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-secondary/60 transition-colors text-left"
                    data-testid={`week-item-${it.id}`}
                  >
                    <span className="text-xs tabular-nums text-muted-foreground w-10 shrink-0">{formatTime(it.at)}</span>
                    <span className={cn("w-1 self-stretch rounded-full shrink-0", overdue ? "bg-danger" : TYPE_COLOR[it.type] || "bg-info")} aria-hidden />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">{it.title || it.player?.name || t("common.event")}</span>
                      <span className="block text-[11px] text-muted-foreground truncate">
                        {it.player?.name ? `${it.player.name}${it.player.club ? ` · ${it.player.club}` : ""}` : it.notes || ""}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </DashboardSectionCard>
  );
}
